/**
 * winePagination.js — Shared wine-listing pagination controls.
 *
 * Parity with old createWinePagination / loadAvailableWinePage (main.js).
 * Used by CardCarousel for every winery when the BE returns a pagination
 * envelope. Always renders when pagination is present (including total_pages
 * === 1) — same as old main.js, which never hid the bar for a single page.
 * Theme via --ws-pagination-* CSS vars (defaults in ChatPanel.css).
 *
 * Click emits: ws-wine-page { pageNo, messageId, listingMessage, listingType,
 *   foodPairingsTaskId }
 */

import { emit } from "../../../core/state.js";

function getVisiblePageCount() {
  const screenWidth = window.visualViewport?.width || window.innerWidth;
  return screenWidth <= 576 ? 4 : 5;
}

/**
 * @param {object} pagination  BE envelope { page_no, total_pages, has_more }
 * @param {object} meta        { messageId, listingMessage, listingType, foodPairingsTaskId }
 * @returns {HTMLElement|null}
 */
export function createWinePagination(pagination = {}, meta = {}) {
  const currentPage = Number(pagination.page_no) || 1;
  // Old main.js: Math.max(total_pages || 0, 1) — always at least page 1.
  const totalPages = Math.max(Number(pagination.total_pages) || 0, 1);
  const hasMore =
    pagination.has_more === false || pagination.has_more === "false"
      ? false
      : currentPage < totalPages;

  const visiblePageCount = getVisiblePageCount();
  let startPage = Math.max(1, currentPage - visiblePageCount + 1);
  let endPage = Math.min(totalPages, startPage + visiblePageCount - 1);
  if (endPage - startPage + 1 < visiblePageCount) {
    startPage = Math.max(1, endPage - visiblePageCount + 1);
  }

  const root = document.createElement("div");
  root.className = "ws-wine-pagination";
  root.setAttribute("aria-label", "Wine listing pages");
  root.dataset.winePagination = JSON.stringify(pagination);

  const fire = (pageNo) => {
    const n = Number(pageNo);
    if (!n || n === currentPage || n < 1 || n > totalPages) return;
    const listing = root.closest(".ws-wine-listing");
    showWinePaginationLoader(listing);
    emit("ws-wine-page", {
      pageNo: n,
      messageId: meta.messageId,
      listingMessage: meta.listingMessage,
      listingType: meta.listingType,
      foodPairingsTaskId: meta.foodPairingsTaskId || "",
      listingEl: listing,
    });
  };

  const mkBtn = (label, pageNo, opts = {}) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ws-wine-page-button" + (opts.arrow ? " ws-wine-page-arrow" : "");
    if (opts.active) btn.classList.add("active");
    btn.dataset.winePage = String(pageNo);
    btn.disabled = !!opts.disabled;
    if (opts.ariaLabel) btn.setAttribute("aria-label", opts.ariaLabel);
    btn.innerHTML = label;
    btn.addEventListener("click", () => fire(pageNo));
    return btn;
  };

  root.appendChild(
    mkBtn("&laquo;", 1, {
      arrow: true,
      disabled: currentPage === 1,
      ariaLabel: "First page",
    }),
  );
  root.appendChild(
    mkBtn("&lsaquo;", currentPage - 1, {
      arrow: true,
      disabled: currentPage === 1,
      ariaLabel: "Previous page",
    }),
  );

  const numbers = document.createElement("div");
  numbers.className = "ws-wine-page-numbers";
  for (let page = startPage; page <= endPage; page++) {
    numbers.appendChild(
      mkBtn(String(page), page, {
        active: page === currentPage,
        disabled: page === currentPage,
      }),
    );
  }
  root.appendChild(numbers);

  root.appendChild(
    mkBtn("&rsaquo;", currentPage + 1, {
      arrow: true,
      disabled: !hasMore,
      ariaLabel: "Next page",
    }),
  );
  root.appendChild(
    mkBtn("&raquo;", totalPages, {
      arrow: true,
      disabled: !hasMore,
      ariaLabel: "Last page",
    }),
  );

  return root;
}

/** Overlay spinner while a page request is in flight. */
export function showWinePaginationLoader(listingEl) {
  if (!listingEl) return null;
  listingEl.classList.add("ws-wine-listing-loading");
  listingEl.style.position = listingEl.style.position || "relative";
  listingEl.querySelectorAll("[data-wine-page]").forEach((btn) => {
    btn.disabled = true;
  });

  const loader = document.createElement("div");
  loader.className = "ws-wine-pagination-loader";
  loader.setAttribute("role", "status");
  loader.setAttribute("aria-label", "Loading wines");
  const spinner = document.createElement("div");
  spinner.className = "ws-wine-page-spinner";
  loader.appendChild(spinner);
  listingEl.appendChild(loader);
  return loader;
}

export function clearWinePaginationLoader(listingEl, loader) {
  if (listingEl) listingEl.classList.remove("ws-wine-listing-loading");
  if (loader) loader.remove();
}
